import React, { useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { LogIn, AlertCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext'
import { ssoNavigation, startWorkspaceSso } from '../services/api';

/**
 * Operator sign-in for the socket-cloud console (arc socket-cloud-operator-sso,
 * sso-04).
 *
 * The only way in is an Aaim Workspace account. This page POSTs same-origin to
 * /api/v1/auth/sso/start, reads the authorization URL the identity provider
 * chose, and hands the browser to it. It sends no identifier, so the request
 * cannot be used to ask whether an account exists, and it receives no token.
 *
 * USER-FACING REMOVAL, stated rather than implied
 * (.claude/rules/consumer-ui-feature-pins.md): the operator email + password
 * form that stood here is deleted, along with its show/hide-password control.
 * It relayed the password to aaim-core, read `success: true`, and ignored
 * `totpRequired: true` -- GH #2285. Google Workspace IS the operator second
 * factor (operator ruling 2026-08-24), so the replacement adds no factor of its
 * own and needs none. The password ROUTES behind that form are still live and
 * are deleted in sso-05; this node removes the surface, not the backend.
 *
 * FAIL CLOSED. A start call that does not answer with an authorization URL
 * leaves the operator on this page with the reason and the button to try again.
 * No local URL is synthesized, and there is no second sign-in path to fall back
 * to.
 */
export const LoginPage: React.FC = () => {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();
  const [startError, setStartError] = useState<string | null>(null);
  const [isStarting, setIsStarting] = useState(false);

  // Redirect if already authenticated
  if (isAuthenticated) {
    const state = location.state as { from?: { pathname?: string } } | null;
    const from = state?.from?.pathname ?? '/';
    return <Navigate to={from} replace />;
  }

  const handleWorkspaceSignIn = async (): Promise<void> => {
    setStartError(null);
    setIsStarting(true);

    try {
      const { authorizationUrl } = await startWorkspaceSso();
      // The browser leaves the SPA here. Nothing after this line runs on a
      // successful start.
      ssoNavigation.go(authorizationUrl);
    } catch (error) {
      setStartError(
        error instanceof Error && error.message
          ? error.message
          : 'Sign-in could not be started. Try again shortly.',
      );
      setIsStarting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-console-bg flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-console-accent border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-console-muted">Initializing...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-console-bg flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-console-card border border-console-border rounded-xl shadow-2xl p-8"
        >
          {/* Header */}
          <div className="text-center mb-8">
            <img
              src="/socketcloud-logo.svg"
              alt="SocketCloud"
              className="h-12 mx-auto mb-6 opacity-90"
            />
            <h1 className="text-2xl font-bold text-console-text mb-2">SocketCloud Console</h1>
            <p className="text-console-muted">Sign in to access your dashboard</p>
          </div>

          {/* Error Message */}
          {startError && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              className="flex items-center gap-2 p-3 mb-6 bg-warning-900/20 border border-warning-500/30 rounded-lg text-warning-400 text-sm"
              data-testid="login-error"
            >
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <span>{startError}</span>
            </motion.div>
          )}

          {/* The only sign-in control on this page. */}
          <button
            type="button"
            onClick={() => void handleWorkspaceSignIn()}
            disabled={isStarting}
            data-testid="workspace-sign-in"
            className="w-full flex items-center justify-center gap-2 px-4 py-3
                     bg-console-accent hover:bg-console-accent/90
                     disabled:bg-console-accent/50 disabled:cursor-not-allowed
                     text-white font-medium rounded-lg transition-colors duration-200"
          >
            {isStarting ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                <span>Redirecting to Aaim...</span>
              </>
            ) : (
              <>
                <LogIn className="w-4 h-4" />
                <span>Sign in with your Aaim Workspace account</span>
              </>
            )}
          </button>

          {/* Identity note: operator sign-in federates to the Aaim IdP */}
          <p className="mt-6 text-xs text-console-muted text-center">
            Aaim operators sign in with their Aaim Workspace account at
            auth.aaim.com. This console issues no password of its own and holds
            no second factor -- your Workspace account carries both.
          </p>

        </motion.div>
      </div>
    </div>
  );
};
